import _ from 'lodash'
import {coreUtils} from '@wix/santa-ds-libs'
import {
    asyncBuildApp,
    buildApp,
    getBuildRecord,
    preBuildApp,
    undraftSite
} from '@wix/ambassador-blocks-v1-blocks-app/http'
import type {
    AsyncBuildAppResponse,
    BuildAppResponse,
    BuildRecord,
    PreBuildAppRequest,
    PreBuildAppResponse,
    UndraftSiteResponse
} from '@wix/ambassador-blocks-v1-blocks-app/types'
import DSErrors from '../../errors/errors'
import codeAppInfoUtils from '../../wixCode/utils/codeAppInfo'
import type {PS} from '@wix/document-services-types'
import {createHttpClient, HttpClient, type IHttpClient} from '@wix/http-client'
import experiment from 'experiment-amd'
import clientSpecMap from '../../siteMetadata/clientSpecMap'
import platformConstants from '../../platform/common/constants'

const {urlUtils} = coreUtils
const {NO_GRID_APP_ID, NO_STUDIO_APP_APP_ID, USER_NOT_AUTHORIZED_FOR_APP, UNKNOWN_SERVER_ERROR} = DSErrors.build
const {SESSION_EXPIRED, NOT_LOGGED_IN, USER_NOT_AUTHORIZED_FOR_SITE} = DSErrors.save

class BuildError extends Error {
    public errorCode: string
    public errorType: string
    public errorDescription: string
    public applicationError: string

    constructor(error) {
        super('Build Error')
        this.errorCode = error.errorCode
        this.errorType = error.errorType
        this.errorDescription = error.errorDescription
        this.applicationError = error.applicationError
    }
}

const generateBuildError = ({
    errorCode = UNKNOWN_ERROR_CODE,
    errorDescription,
    errorType = BUILD_ERROR_TYPES[errorCode],
    applicationError = undefined
}: {
    errorCode?: keyof typeof BUILD_ERROR_TYPES
    errorDescription: string
    errorType?: string
    applicationError?: string
}) =>
    new BuildError({
        errorCode,
        errorDescription,
        errorType: errorCode === UNKNOWN_ERROR_CODE ? errorType : BUILD_ERROR_TYPES[errorCode],
        applicationError
    })

export const UNKNOWN_ERROR_CODE = '-1'
const BUILD_ERROR_TYPES = {
    '-1': UNKNOWN_SERVER_ERROR,
    '-12': SESSION_EXPIRED,
    '-15': NOT_LOGGED_IN,
    '-17': USER_NOT_AUTHORIZED_FOR_SITE,
    '-10198': NO_GRID_APP_ID,
    '-15053': NO_STUDIO_APP_APP_ID,
    '-15054': USER_NOT_AUTHORIZED_FOR_APP
}

const getNetworkErrorData = error => {
    if (HttpClient.isHttpError(error)) {
        return generateBuildError({
            errorCode: error.response.data.errorCode,
            errorDescription: error.response.data.errorDescription || error.response.data.message,
            applicationError: error.response.data.details?.applicationError
        })
    }
    return generateBuildError({
        errorCode: UNKNOWN_ERROR_CODE,
        errorDescription: 'Unknown error'
    })
}
const createWixHttpClientInstance = (ps: PS): IHttpClient => {
    const {editorRootUrl} = ps.dal.get(ps.pointers.general.getServiceTopology())
    return createHttpClient({
        baseURL: editorRootUrl,
        getAppToken: () => {
            if (experiment.isOpen('dm_useMetaSiteInstanceForBlocksApis')) {
                const appData = clientSpecMap.getAppData(ps, platformConstants.APPS.META_SITE.applicationId)
                return appData.instance
            }
            const codeAppInfo = codeAppInfoUtils.getCodeAppInfoFromPS(ps)
            return codeAppInfo.signedInstance
        }
    })
}

const getWixHttpClientInstance = _.once(createWixHttpClientInstance)

const preBuild = async (
    ps: PS,
    appDefId: string,
    documentWidgets?: PreBuildAppRequest['documentWidgets']
): Promise<PreBuildAppResponse> => {
    try {
        const wixHttpClientInstance = getWixHttpClientInstance(ps)
        const response = await wixHttpClientInstance.request(preBuildApp({appDefId, documentWidgets}))
        return response.data
    } catch (error) {
        if (HttpClient.isHttpError(error)) {
            throw new Error(`Cannot preBuild app: ${error.response.data.errorDescription}`)
        }
        throw new Error('Cannot preBuild app with unknown reason')
    }
}

const unDraftSite = async (
    ps: PS,
    {metaSiteId, appName}: {metaSiteId: string; appName: string}
): Promise<UndraftSiteResponse> => {
    try {
        const wixHttpClientInstance = getWixHttpClientInstance(ps)
        const appDefId = urlUtils.getParameterByName('appDefinitionId') || undefined
        const response = await wixHttpClientInstance.request(
            undraftSite({
                metaSiteId,
                appName,
                appDefId
            })
        )
        return response.data
    } catch (error) {
        console.error('Error when unDrafting site', error)
        if (HttpClient.isHttpError(error)) {
            throw new Error(
                `Cannot unDraft site: ${error.response.data.errorDescription || error.response.data.message}`
            )
        }
        throw new Error('Cannot unDraft site with unknown reason')
    }
}

const getBuildRecordById = async (ps: PS, buildId: string, appDefId: string): Promise<BuildRecord> => {
    const wixHttpClientInstance = getWixHttpClientInstance(ps)
    try {
        const response = await wixHttpClientInstance.request(getBuildRecord({buildId, appDefId}))
        return response.data.buildRecord
    } catch (error) {
        throw getNetworkErrorData(error)
    }
}

const asyncBuild = async (
    ps: PS,
    appDefId: string,
    {widgetsMap, appMarketingName, blocksVersion, versionType, codePackage, dependencies, releaseNotes, publishRc}
): Promise<AsyncBuildAppResponse> => {
    try {
        const wixHttpClientInstance = getWixHttpClientInstance(ps)
        const response = await wixHttpClientInstance.request(
            asyncBuildApp({
                appDefId,
                appMarketingName,
                blocksVersion,
                components: {
                    widgetsMap,
                    codePackage
                },
                versionType: versionType?.toUpperCase(),
                dependencies,
                releaseNotes,
                publishRc
            })
        )

        return response.data
    } catch (error) {
        if (error instanceof BuildError) {
            throw error
        }
        throw getNetworkErrorData(error)
    }
}

const build = async (
    ps: PS,
    appDefId: string,
    {widgetsMap, appMarketingName, blocksVersion, versionType, publishRc}
): Promise<BuildAppResponse> => {
    try {
        const wixHttpClientInstance = getWixHttpClientInstance(ps)
        const response = await wixHttpClientInstance.request(
            buildApp({
                appDefId,
                appMarketingName,
                blocksVersion,
                widgetsMap,
                versionType: versionType.toUpperCase(),
                publishRc
            })
        )
        return response.data
    } catch (error) {
        if (error instanceof BuildError) {
            throw error
        }
        throw getNetworkErrorData(error)
    }
}

export default {
    build,
    preBuild,
    asyncBuild,
    unDraftSite,
    generateBuildError,
    getBuildRecordById
}
